import { memo, useRef } from 'react';

import Button from 'bloko/blocks/button';
import ButtonSelect from 'bloko/blocks/button/ButtonSelect';
import { SingleCalendarPicker } from 'bloko/blocks/calendar';
import { toDate, toISO, YearMonthDayString } from 'bloko/blocks/calendar/datesHelper';
import { ClickDown } from 'bloko/blocks/drop';
import { DownPlacement } from 'bloko/blocks/drop/Down';
import VSpacing from 'bloko/blocks/vSpacing';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import formatDate from 'Modules/formatDate';
import translation from 'src/components/translation';
import useToggleState from 'src/hooks/useToggleState';
import { START_AFTER_PAYMENT, StartAfterPayment } from 'src/models/price/product.types';

const TrlKeys = {
    startAfterPayment: 'price.tariffField.activationDate.startAfterPayment',
};

const TODAY: YearMonthDayString = toISO(new Date());

const ActivationDate: TranslatedComponent<{
    value?: YearMonthDayString | StartAfterPayment;
    minValue?: YearMonthDayString;
    maxValue?: YearMonthDayString;

    onChange: (date: YearMonthDayString | StartAfterPayment) => void;
}> = ({ trls, value = START_AFTER_PAYMENT, minValue, maxValue, onChange }) => {
    const [showClickDown, toggleClickDown] = useToggleState(false);
    const activatorRef = useRef(null);
    const getButtonText = (): string => {
        if (value === START_AFTER_PAYMENT) {
            return trls[TrlKeys.startAfterPayment];
        }
        const date = toDate(value);
        return formatDate(date, 'dd.MM.yyyy');
    };
    return (
        <ClickDown
            placement={DownPlacement.BottomStart}
            onlySetPlacement
            show={showClickDown}
            render={(close) => (
                <>
                    <SingleCalendarPicker
                        initialDate={value || TODAY}
                        onDateClick={(date: YearMonthDayString | undefined) => {
                            toggleClickDown();
                            if (date !== undefined) {
                                onChange(date);
                            }
                            close?.();
                        }}
                        disableDaysAfterDate={maxValue}
                        disableDaysBeforeDate={minValue}
                    />
                    <VSpacing base={5} />
                    <Button
                        stretched
                        onClick={() => {
                            toggleClickDown();
                            onChange(START_AFTER_PAYMENT);
                            close?.();
                        }}
                    >
                        {trls[TrlKeys.startAfterPayment]}
                    </Button>
                </>
            )}
            activatorRef={activatorRef}
        >
            <ButtonSelect
                onClick={() => toggleClickDown()}
                data-qa="tariff-field-activation-date"
                innerRef={activatorRef}
            >
                <span suppressHydrationWarning>{getButtonText()}</span>
            </ButtonSelect>
        </ClickDown>
    );
};

export default memo(translation(ActivationDate));
